/**
 * Gate 2 ΓÇö Test Execution & Evidence Collection.
 *
 * One route, four internal steps (see gate2Tokens.GATE2_STEPS). Scope selection,
 * build declarations and execution state are held here so stepping between
 * Execution Scope and Assign & Execute does not lose them.
 *
 * Scope data is real: suites come from the Gate 1 test-suite API and test cases
 * (including their ground truth) from the test-case API. Observed outcomes are
 * the one thing this page cannot source yet ΓÇö the execution backend does not
 * exist, so every in-scope case reports `pending` instead of a fabricated
 * result.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import StickyPageHeaderWithBack from "../../components/layout/StickyPageHeaderWithBack";
import TruncationTooltip from "../../components/TruncationTooltip";
import {
  focusGate2MainContent,
  GATE2_MAIN_ID,
  GATE2_SKIP_TO_MAIN_SX,
} from "./gate2A11y";
import AppToast from "../../components/feedback/AppToast";
import { fetchTestSuites } from "../../services/testSuiteClient";
import { fetchTestCases } from "../../services/testCaseClient";
import AddTestCaseDialog from "../gate1/AddTestCaseDialog";
import { fetchTrustWeights } from "../../services/trustWeightsClient";
import {
  collectExecutionEvidence,
  fetchGate2ConnectionConfig,
  getExecutionRunResults,
  listExecutionRuns,
  logTrustScoreBreakdown,
} from "../../services/gate2ExecutionClient";
import {
  metricResultsFromScores,
  readGate2Session,
  retrievalMapFromRunResults,
  selectionFromRun,
  writeGate2Session,
} from "./gate2SessionState";
import { unwrapResponseText } from "../../utils/unwrapResponseText";
import {
  CONNECTORS,
  activeConnectors,
  connectorsComplete,
  connectorVerified,
  connectorsForMetricItem,
  scopeConnectorsHint,
  unionRequiredConnectors,
} from "../../utils/metricConnectors";
import { isBlockedExtraCgMetric, isGate2ExecutableMetric } from "../../utils/executableMetrics";
import { GATE1_CONNECTOR_SURFACE, gate1ReadinessConnectorPath } from "./gate1ConnectorNav";
import {
  EMPTY_API_CONNECTION_FORM,
  EMPTY_CREDENTIALS as EMPTY_API_CREDENTIALS,
  apiFormComplete,
  apiFormFromGate1Config,
  buildApiPayload,
  readinessBearerPrompt,
} from "./gate2ApiForm";
import {
  EMPTY_TRACE_CREDENTIALS,
  EMPTY_TRACE_FORM,
  buildTracePayload,
  traceCredentialsFromConfig,
  traceFormComplete,
  traceFormFromGate1Config,
} from "../../utils/traceSurfaceForm";
import {
  EMPTY_VECTOR_CREDENTIALS,
  EMPTY_VECTOR_FORM,
  buildRetrievePayload,
  formFromGate1Config,
  isVectorStoreProviderUnavailable,
  vectorFormComplete,
} from "./gate2VectorForm";
import { vectorCredentialsFromConfig } from "../../utils/vectorStoreSurfaceForm";
import VectorConnectionAlert from "./VectorConnectionAlert";
import QualityScorecardStep from "./QualityScorecardStep";
import { categoryLabel, formatItemThreshold } from "./ExecutionScopeItemDetailView";
import Gate2DecisionStep, { canSubmitGate2Decision } from "./Gate2DecisionStep";
import { buildScorecardSummary, trustScoreBreakdown } from "./gate2ScorecardSummary";
import { numberTestCases } from "../../utils/testCaseCodes";
import ExecutionScopeStep from "./ExecutionScopeStep";
import DeclareBuildDialog from "./DeclareBuildDialog";
import Gate2ExecutingMetricsDialog from "./Gate2ExecutingMetricsDialog";
import Gate2ExecutingCustomTestCaseDialog from "./Gate2ExecutingCustomTestCaseDialog";
import { executeCustomTestCase } from "./gate2CustomTestCaseRun";
import {
  flattenExecutionJobs,
  listProgressMetrics,
  metricPassed,
  scoreSingleJob,
} from "./gate2ExecutionScoring";
import {
  GATE2_STEPS,
  SCOPE_CANCEL_BTN_SX,
  PRIMARY_BTN_SX,
  STEP_STATE_CHIP,
  STEP_RAIL_LABEL_DEFAULT_SX,
  STEP_RAIL_LABEL_IN_PROGRESS_SX,
  STEP_RAIL_IN_PROGRESS_CHIP_SX,
  STEP_RAIL_COMPLETED_CHIP_SX,
  STEP_RAIL_PLANNED_CHIP_SX,
  STEP_RAIL_ROW_ACTIVE_SX,
  STEP_RAIL_ROW_BASE_SX,
  STEP_RAIL_ROW_NAVIGABLE_SX,
  STEP_RAIL_ROW_PLANNED_SX,
} from "./gate2Tokens";

const ACTIVE_GATE = 2;

/** Fixed Gate 2 policy — not project data, so these are constants, not fetched. */
const EXECUTION_PATHS = ["Native", "Jenkins"];
const EVIDENCE_TIERS = ["Independently witnessed", "Self-reported"];
const DECISION_QUORUM = ["Head of Quality", "Risk & Compliance"];

/** Decision step is reachable from the scorecard so later gates can be opened from here. */
const GATE2_DECISION_ENABLED = true;

const GATE2_SHELL_PAGE_SUBTITLE =
  "Execute approved test suites, collect evidence, compute scorecards, and make the Gate 2 release decision.";

const GATE2_DEFINE_SCOPE_SUBTITLE =
  "Select which approved Gate 1B Test Suites or Metrics apply to this run, then declare the exact build and environment they will be tested against.";

function Gate2ScopeStickyHeader({ onBack }) {
  return (
    <StickyPageHeaderWithBack
      onBack={onBack}
      backAriaLabel="Back to test suites"
      title="Defining the Execution Scope"
      subtitle={GATE2_DEFINE_SCOPE_SUBTITLE}
    />
  );
}

function Gate2PageTitleBar({ onBack, backAriaLabel = "Back to test suites" }) {
  return (
    <StickyPageHeaderWithBack
      onBack={onBack}
      backAriaLabel={backAriaLabel}
      title="Test Execution & Evidence Collection"
      subtitle={GATE2_SHELL_PAGE_SUBTITLE}
    />
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function StatBlock({ value, label, detail, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: "Lato, sans-serif",
          fontSize: 20,
          fontWeight: 800,
          lineHeight: "100%",
          color,
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
          {label}
        </Typography>
        <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 11, color: "#94A3B8" }}>
          {detail}
        </Typography>
      </Box>
    </Box>
  );
}

function Gate2MetricDetailHeader({ item, onBack }) {
  const caseCount = item?.cases?.length || 0;
  return (
    <StickyPageHeaderWithBack onBack={onBack} backAriaLabel="Back to evidence" title={item?.name || "Metric"}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "24px", color: "#383838" }}>
        <Typography component="span" sx={{ fontFamily: "Lato, sans-serif", fontSize: 14, lineHeight: "100%", color: "#383838", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{caseCount}</Box>
          <Box component="span" sx={{ fontWeight: 400 }}>{` Test case${caseCount === 1 ? "" : "s"}`}</Box>
        </Typography>
        <Typography component="span" sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, lineHeight: "100%", color: "#383838", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>Category : </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>{categoryLabel(item)}</Box>
        </Typography>
        <Typography component="span" sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, lineHeight: "100%", color: "#383838", whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ fontWeight: 700 }}>Threshold : </Box>
          <Box component="span" sx={{ fontWeight: 400 }}>{formatItemThreshold(item, "metric")}</Box>
        </Typography>
      </Box>
    </StickyPageHeaderWithBack>
  );
}

function Gate2ReviewStickyHeader({ onBack }) {
  return (
    <StickyPageHeaderWithBack
      onBack={onBack}
      backAriaLabel="Back to define scope"
      title="Review & Execute"
      subtitle="Review your selected metrics below and proceed to execute the evaluation run."
    />
  );
}

function stepRailStatusPillSx(state, isActive) {
  const chipSx =
    isActive
      ? STEP_RAIL_IN_PROGRESS_CHIP_SX
      : state === "planned"
        ? STEP_RAIL_PLANNED_CHIP_SX
        : STEP_RAIL_COMPLETED_CHIP_SX;
  return {
    ...chipSx,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}

function StepRailRowVisual({ step, isActive, isDone, state, chipLabel }) {
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            bgcolor: isActive ? "#FFFFFF" : isDone ? "#22C55E" : "#CBD5E1",
          }}
        />
        <TruncationTooltip title={step.label}>
          <Typography
            component="span"
            sx={{
              ...(isActive ? STEP_RAIL_LABEL_IN_PROGRESS_SX : STEP_RAIL_LABEL_DEFAULT_SX),
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
              display: "block",
            }}
          >
            {step.label}
          </Typography>
        </TruncationTooltip>
      </Box>
      <Box component="span" aria-hidden sx={stepRailStatusPillSx(state, isActive)}>
        {chipLabel}
      </Box>
    </>
  );
}

/** Step list: only completed steps are tab stops; current step is aria-current; planned steps are inert. */
function StepRail({ steps, activeStepId, completedStepIds, onSelect, layout = "card" }) {
  const isSidebar = layout === "sidebar";
  return (
    <Box
      component="nav"
      aria-label="Gate 2 workflow steps"
      sx={{
        width: { xs: "100%", md: isSidebar ? 244 : 240 },
        minWidth: { md: isSidebar ? 244 : 240 },
        flexShrink: 0,
        alignSelf: isSidebar ? "stretch" : undefined,
        height: isSidebar ? "100%" : "auto",
        bgcolor: "#FFFFFF",
        border: isSidebar ? "none" : "1px solid #E6E6E6",
        borderRight: isSidebar ? "1px solid #E6E6E6" : undefined,
        borderRadius: isSidebar ? 0 : "4px",
        overflow: "auto",
        boxSizing: "border-box",
      }}
    >
      <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {steps.map((step) => {
          const isActive = step.id === activeStepId;
          const isDone = completedStepIds.has(step.id);
          const isPlanned = !isActive && !isDone;
          const canActivate = isDone;
          const state = isActive ? "active" : isDone ? "established" : "planned";
          const chipLabel = STEP_STATE_CHIP[state].label;

          return (
            <Box
              key={step.id}
              component="li"
              aria-current={isActive ? "step" : undefined}
              sx={{ display: "block" }}
            >
              {canActivate ? (
                <Box
                  component="button"
                  type="button"
                  aria-label={`${step.label}, ${chipLabel}. Go to this step.`}
                  onClick={() => onSelect(step.id)}
                  sx={{
                    ...STEP_RAIL_ROW_BASE_SX,
                    ...(isActive
                      ? {
                          ...STEP_RAIL_ROW_ACTIVE_SX,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "#004ACC" },
                        }
                      : STEP_RAIL_ROW_NAVIGABLE_SX),
                  }}
                >
                  <StepRailRowVisual
                    step={step}
                    isActive={isActive}
                    isDone={isDone}
                    state={state}
                    chipLabel={chipLabel}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    ...STEP_RAIL_ROW_BASE_SX,
                    ...(isActive ? STEP_RAIL_ROW_ACTIVE_SX : {}),
                    ...(isPlanned ? STEP_RAIL_ROW_PLANNED_SX : {}),
                  }}
                >
                  <StepRailRowVisual
                    step={step}
                    isActive={isActive}
                    isDone={isDone}
                    state={state}
                    chipLabel={chipLabel}
                  />
                  {isActive ? (
                    <Typography component="span" sx={visuallyHiddenSx}>
                      {`, ${chipLabel}, current step`}
                    </Typography>
                  ) : isPlanned ? (
                    <Typography component="span" sx={visuallyHiddenSx}>
                      {`, ${chipLabel}, not yet available`}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const visuallyHiddenSx = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

export default function Gate2ExecutionPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const openOnDecision = location.state?.stepId === "decision";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [suites, setSuites] = useState([]);
  const [allTestCases, setAllTestCases] = useState([]);
  const [risks, setRisks] = useState([]);
  const [metrics, setMetrics] = useState([]);

  const [stepId, setStepId] = useState(openOnDecision ? "decision" : "scope");
  /** assign step: review (confirmed metrics) then execute (evidence collection UI). */
  const [assignPhase, setAssignPhase] = useState("review");
  const [completedStepIds, setCompletedStepIds] = useState(() => new Set());
  const [scopeMode, setScopeMode] = useState("metric");
  // Selection is per scope mode: switching Test Suite Γåö Metric changes what an
  // id means, so the two sets must not bleed into each other.
  const [selectedBySuite, setSelectedBySuite] = useState(() => new Set());
  const [selectedByMetric, setSelectedByMetric] = useState(() => new Set());
  const [builds, setBuilds] = useState(() => new Map());
  const [detailItemId, setDetailItemId] = useState("");
  const [buildDialogItem, setBuildDialogItem] = useState(null);

  const [running, setRunning] = useState(false);
  const [connectionAlert, setConnectionAlert] = useState(null);
  const [isContentGenerator, setIsContentGenerator] = useState(false);
  const [appEndpoint, setAppEndpoint] = useState({ available: false });
  const [apiForm, setApiForm] = useState(EMPTY_API_CONNECTION_FORM);
  const [apiCredentials, setApiCredentials] = useState(EMPTY_API_CREDENTIALS);
  const [gate1ApiSource, setGate1ApiSource] = useState(null);
  const [vectorForm, setVectorForm] = useState(EMPTY_VECTOR_FORM);
  const [vectorCredentials, setVectorCredentials] = useState(EMPTY_VECTOR_CREDENTIALS);
  const [gate1Source, setGate1Source] = useState(null);
  const [traceForm, setTraceForm] = useState({ ...EMPTY_TRACE_FORM });
  const [traceCredentials, setTraceCredentials] = useState({ ...EMPTY_TRACE_CREDENTIALS });
  const [gate1TraceSource, setGate1TraceSource] = useState(null);
  const [scopeNote, setScopeNote] = useState("");
  /** test case id ΓåÆ { outcome, chunks, error, prompt } from the last run. */
  const [retrievalByCaseId, setRetrievalByCaseId] = useState(() => new Map());
  /** One scored metric per selected suite, shown on the Metrics Summary step. */
  const [metricResults, setMetricResults] = useState([]);
  const [scoring, setScoring] = useState(false);
  /** Server-side id of the current execution run; scores attach to it. */
  const [runId, setRunId] = useState(null);
  const [targetTrustIndex, setTargetTrustIndex] = useState(null);
  const [trustWeights, setTrustWeights] = useState(null);
  const [scorecardToastOpen, setScorecardToastOpen] = useState(false);
  const [customTestCaseFlyoutOpen, setCustomTestCaseFlyoutOpen] = useState(false);
  const [customTestCaseToast, setCustomTestCaseToast] = useState("");
  const [customExecOpen, setCustomExecOpen] = useState(false);
  const [customExecProgress, setCustomExecProgress] = useState(0);
  /** Run Once trials — count only, no table rows */
  const [ephemeralCustomRunCount, setEphemeralCustomRunCount] = useState(0);
  /** Save & Execute — shown on Custom Test Cases tab */
  const [savedCustomTestResults, setSavedCustomTestResults] = useState([]);
  const [decisionVote, setDecisionVote] = useState("conditional_go");
  const [decisionRationale, setDecisionRationale] = useState("");
  const [decisionAttestation, setDecisionAttestation] = useState(false);
  const [decisionSubmitNotice, setDecisionSubmitNotice] = useState("");
  const [scorecardMetricDetailOpen, setScorecardMetricDetailOpen] = useState(false);
  const [scorecardMetricDetail, setScorecardMetricDetail] = useState(null);
  const closeScorecardMetricDetailRef = useRef(() => {});
  const handleScorecardMetricDetailChange = useCallback((open, close, item) => {
    setScorecardMetricDetailOpen(Boolean(open));
    setScorecardMetricDetail(open ? item : null);
    closeScorecardMetricDetailRef.current = typeof close === "function" ? close : () => {};
  }, []);
  /** Figma execution overlay — evidence + scoring per selected metric. */
  const [executionDialog, setExecutionDialog] = useState({
    open: false,
    phase: "running",
    metrics: [],
    passCount: 0,
    failCount: 0,
  });
  const executionCancelRef = useRef(false);
  const persistReadyRef = useRef(false);

  const load = useCallback(async () => {
    persistReadyRef.current = false;
    try {
      setLoading(true);
      setError("");
      const [suitesRes, casesRes, connectionRes, trustRes] = await Promise.all([
        fetchTestSuites(projectId),
        fetchTestCases(projectId),
        fetchGate2ConnectionConfig(projectId).catch(() => null),
        fetchTrustWeights(projectId).catch(() => null),
      ]);
      if (!suitesRes?.success) {
        setError(suitesRes?.error || "Failed to load test suites.");
        return;
      }
      setSuites(suitesRes.data?.suites || []);
      setRisks(suitesRes.data?.risks || []);
      if (casesRes?.success) {
        setAllTestCases(casesRes.data?.testCases || []);
        setMetrics(casesRes.data?.metrics || []);
      }

      if (trustRes?.success && trustRes.data?.targetTrustIndex != null) {
        setTargetTrustIndex(Number(trustRes.data.targetTrustIndex));
      }
      if (trustRes?.success && trustRes.data?.trustWeights) {
        setTrustWeights(trustRes.data.trustWeights);
      }

      const connections = connectionRes?.success ? connectionRes.data : null;
      const gate1Api = connections?.api || null;
      setGate1ApiSource(gate1Api);
      if (gate1Api?.available) {
        setApiForm(apiFormFromGate1Config(gate1Api.config));
      } else {
        setApiForm(EMPTY_API_CONNECTION_FORM);
        setApiCredentials(EMPTY_API_CREDENTIALS);
      }
      const gate1Vector = connections?.vector || null;
      const weaviateUnavailable = isVectorStoreProviderUnavailable(gate1Vector?.config?.provider);
      setGate1Source(weaviateUnavailable && gate1Vector ? { ...gate1Vector, available: false } : gate1Vector);
      if (gate1Vector?.available && !weaviateUnavailable) {
        setVectorForm(formFromGate1Config(gate1Vector.config));
        setVectorCredentials(vectorCredentialsFromConfig(gate1Vector.config));
      } else {
        setVectorForm(EMPTY_VECTOR_FORM);
        setVectorCredentials(EMPTY_VECTOR_CREDENTIALS);
      }
      const gate1Trace = connections?.traces || null;
      setGate1TraceSource(gate1Trace);
      if (gate1Trace?.available) {
        setTraceForm(traceFormFromGate1Config(gate1Trace.config));
        setTraceCredentials(traceCredentialsFromConfig(gate1Trace.config));
      } else {
        setTraceForm({ ...EMPTY_TRACE_FORM });
        setTraceCredentials({ ...EMPTY_TRACE_CREDENTIALS });
      }
      const hasCgCatalogMetric = (casesRes?.success ? casesRes.data?.metrics || [] : []).some(
        (m) =>
          String(m.metricId || m.metric_id || "").startsWith("cg_") ||
          (Array.isArray(m.families) &&
            m.families.length > 0 &&
            m.families.every((f) => String(f) === "content_generator"))
      );
      setIsContentGenerator(
        Boolean(connections?.isContentGenerator) ||
          connections?.executionMode === "content_generator" ||
          hasCgCatalogMetric
      );
      setAppEndpoint(connections?.appEndpoint || { available: false });

      const saved = readGate2Session(projectId);
      const metricsList = casesRes?.success ? casesRes.data?.metrics || [] : [];
      const testCases = casesRes?.success ? casesRes.data?.testCases || [] : [];
      let restoredRun = null;
      try {
        const runsRes = await listExecutionRuns(projectId, 1);
        const latest = runsRes?.data?.runs?.[0];
        if (latest?.id) {
          const detail = await getExecutionRunResults(projectId, latest.id);
          if (detail?.success) restoredRun = detail.data;
        }
      } catch {
        /* keep wizard usable even if run history is unavailable */
      }

      if (restoredRun?.run?.id) setRunId(restoredRun.run.id);
      else if (saved?.runId) setRunId(saved.runId);

      if (restoredRun?.results?.length) {
        setRetrievalByCaseId(retrievalMapFromRunResults(restoredRun.results));
      }
      if (restoredRun?.scores?.length) {
        setMetricResults(metricResultsFromScores(restoredRun.scores));
      }

      const sel = selectionFromRun({
        scores: restoredRun?.scores,
        results: restoredRun?.results,
        metrics: metricsList,
        testCases,
      });
      if (saved?.selectedByMetric?.length) setSelectedByMetric(new Set(saved.selectedByMetric));
      else if (sel.byMetric.size) setSelectedByMetric(sel.byMetric);
      if (saved?.selectedBySuite?.length) setSelectedBySuite(new Set(saved.selectedBySuite));
      else if (sel.bySuite.size) setSelectedBySuite(sel.bySuite);
      if (saved?.scopeMode === "suite" || saved?.scopeMode === "metric") {
        setScopeMode(saved.scopeMode);
      } else if (sel.bySuite.size && !sel.byMetric.size) {
        setScopeMode("suite");
      }

      const completed = new Set(saved?.completedStepIds || []);
      if (restoredRun?.scores?.length) {
        completed.add("scope");
        completed.add("assign");
        completed.add("review");
      } else if (restoredRun?.results?.length) {
        completed.add("scope");
        completed.add("assign");
      }
      if (saved?.decisionVote) completed.add("decision");
      setCompletedStepIds(completed);

      if (saved?.stepId) setStepId(saved.stepId);
      else if (completed.has("decision")) setStepId("decision");
      else if (completed.has("review")) setStepId("review");
      else if (completed.has("assign")) setStepId("assign");

      if (saved?.decisionVote) setDecisionVote(saved.decisionVote);
      if (typeof saved?.decisionRationale === "string") setDecisionRationale(saved.decisionRationale);
      if (saved?.decisionAttestation) setDecisionAttestation(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to load the execution scope.");
    } finally {
      persistReadyRef.current = true;
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    load();
  }, [projectId, load]);

  useEffect(() => {
    if (!projectId || loading || !persistReadyRef.current) return;
    writeGate2Session(projectId, {
      stepId,
      scopeMode,
      selectedByMetric: [...selectedByMetric],
      selectedBySuite: [...selectedBySuite],
      completedStepIds: [...completedStepIds],
      decisionVote,
      decisionRationale,
      decisionAttestation,
      runId,
    });
  }, [
    projectId,
    loading,
    stepId,
    scopeMode,
    selectedByMetric,
    selectedBySuite,
    completedStepIds,
    decisionVote,
    decisionRationale,
    decisionAttestation,
    runId,
  ]);

  /** Verified pool, decorated with the same TC/Risk codes the Gate 1 screens show. */
  const numberedById = useMemo(() => {
    const metricIds = new Set(metrics.map((m) => String(m.id)));
    const riskIds = new Set(risks.map((r) => String(r.id)));
    const verified = allTestCases.filter((tc) => {
      if (tc.status !== "verified" || tc.enabled === false) return false;
      if (tc.metricId && metricIds.size && !metricIds.has(String(tc.metricId))) return false;
      if (tc.riskId && riskIds.size && !riskIds.has(String(tc.riskId))) return false;
      return true;
    });
    return new Map(numberTestCases(verified, risks).map((tc) => [String(tc.id), tc]));
  }, [allTestCases, risks, metrics]);

  const metricsById = useMemo(() => {
    const map = new Map();
    for (const m of metrics) {
      if (m?.id != null) map.set(String(m.id), m);
    }
    return map;
  }, [metrics]);

  const suiteItems = useMemo(
    () =>
      suites
        .map((suite) => {
          const cases = (suite.cases || [])
            .map((member) => numberedById.get(String(member.testCaseId)))
            .filter(Boolean);
          const executableCases = cases.filter((tc) => {
            const metric = metricsById.get(String(tc.metricId));
            return isGate2ExecutableMetric(
              metric?.metricId || metric?.formulaFn,
              metric?.metricName || tc.metricName
            );
          });
          const blockedBySuiteName = isBlockedExtraCgMetric("", suite.name);
          const allLinkedExecutable =
            cases.length > 0 && executableCases.length === cases.length;
          return {
            id: suite.id,
            name: suite.name,
            cases,
            executableCases: blockedBySuiteName ? [] : executableCases,
            executable: !blockedBySuiteName && allLinkedExecutable,
            requiredConnectors: unionRequiredConnectors(
              blockedBySuiteName
                ? []
                : executableCases.map((tc) => metricsById.get(String(tc.metricId))).filter(Boolean)
            ),
            catalogMetricId: null,
          };
        })
        .filter((item) => item.cases.length > 0),
    [suites, numberedById, metricsById]
  );

  const metricItems = useMemo(() => {
    const byMetric = new Map();
    numberedById.forEach((tc) => {
      if (!tc.metricId) return;
      const key = String(tc.metricId);
      if (!byMetric.has(key)) byMetric.set(key, []);
      byMetric.get(key).push(tc);
    });
    return metrics
      .map((m) => {
        const cases = byMetric.get(String(m.id)) || [];
        const executable = isGate2ExecutableMetric(m.metricId || m.formulaFn, m.metricName);
        return {
          id: m.id,
          name: m.metricName,
          metricName: m.metricName,
          catalogMetricId: m.metricId,
          formulaFn: m.formulaFn,
          families: m.families || [],
          implementationTier: m.implementationTier,
          threshold: m.threshold,
          direction: m.direction,
          unit: m.unit,
          thresholdJson: m.thresholdJson,
          cases,
          executableCases: executable ? cases : [],
          executable,
          requiredConnectors: executable ? connectorsForMetricItem(m) : [],
        };
      })
      .filter((item) => item.cases.length > 0)
      .sort((a, b) => Number(b.executable) - Number(a.executable));
  }, [metrics, numberedById]);

  const items = scopeMode === "suite" ? suiteItems : metricItems;
  const selectedIds = scopeMode === "suite" ? selectedBySuite : selectedByMetric;
  const setSelectedIds = scopeMode === "suite" ? setSelectedBySuite : setSelectedByMetric;

  const toggleSelect = (id) => {
    const item = items.find((row) => row.id === id);
    if (item && item.executable === false) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const ids = items.filter((item) => item.executable !== false).map((item) => item.id);
      const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
      return allOn ? new Set() : new Set(ids);
    });
  };

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.id) && item.executable !== false),
    [items, selectedIds]
  );

  const requiredConnectors = useMemo(
    () => unionRequiredConnectors(selectedItems),
    [selectedItems]
  );

  const connectorChecks = {
    api: connectorVerified(
      gate1ApiSource,
      apiFormComplete(apiForm, apiCredentials, { bearerTokenSaved: gate1ApiSource?.bearerTokenSaved })
    ),
    vector: connectorVerified(gate1Source, vectorFormComplete(vectorForm)),
    traces: connectorVerified(
      gate1TraceSource,
      Boolean(traceForm.provider) &&
        (traceFormComplete(traceForm, traceCredentials) || Boolean(gate1TraceSource?.available))
    ),
  };

  const optionalConnectorIds = [CONNECTORS.TRACES];
  const canRun =
    selectedItems.length > 0 &&
    !running &&
    connectorsComplete(requiredConnectors, connectorChecks, { optionalIds: optionalConnectorIds }) &&
    (!isContentGenerator || Boolean(appEndpoint.available));

  const scopeFooterHint = isContentGenerator
    ? "Select at least one suite. An application endpoint is required; a vector database is optional."
    : scopeConnectorsHint(requiredConnectors, {
        hasSelection: selectedItems.length > 0,
        canRun,
        optionalIds: optionalConnectorIds,
      });

  const inferredAnswerSource = requiredConnectors.includes(CONNECTORS.REST_API) ? "api" : "";

  /** In-scope cases, deduped: one card per test case even if two suites share it. */
  const executionCases = useMemo(() => {
    const seen = new Map();
    selectedItems.forEach((item) =>
      (item.executableCases || item.cases).forEach((tc) => {
        if (seen.has(tc.id)) return;
        const retrieval = retrievalByCaseId.get(tc.id);
        seen.set(tc.id, {
          id: tc.id,
          code: tc.code,
          name: tc.name,
          metricId: tc.metricId,
          prompt: retrieval?.prompt || (Array.isArray(tc.input) ? tc.input[0] : "") || "",
          expected: unwrapResponseText(
            tc.groundTruth?.expected_output || tc.groundTruth?.expected_behavior || ""
          ),
          extras: retrieval?.extras || tc.groundTruth?.extras || {},
          path: EXECUTION_PATHS[0],
          metricName: retrieval?.metricName || tc.metricName || "",
          outcome: retrieval?.outcome || "pending",
          answer: retrieval?.answer || "",
          chunks: retrieval?.chunks || [],
          askOk: retrieval?.askOk || false,
          askError: retrieval?.askError || null,
          statusCode: retrieval?.statusCode ?? null,
          latencyMs: retrieval?.latencyMs ?? null,
          traces: retrieval?.traces || [],
          evidence: retrieval?.evidence || null,
          matchCount: retrieval?.matchCount ?? 0,
          usableCount: retrieval?.usableCount ?? 0,
          queried: retrieval?.queried || null,
          diagnostics: retrieval?.diagnostics || null,
          error: retrieval?.error || null,
          connectors: retrieval?.connectors || requiredConnectors,
          answerSource: retrieval?.answerSource || inferredAnswerSource,
        });
      })
    );
    return Array.from(seen.values());
  }, [selectedItems, retrievalByCaseId, requiredConnectors, inferredAnswerSource]);

  const buildExecutionCasesSnapshot = useCallback(
    (retrievalMap) => {
      const seen = new Map();
      selectedItems.forEach((item) =>
        (item.executableCases || item.cases).forEach((tc) => {
          if (seen.has(tc.id)) return;
          const numbered = numberedById.get(String(tc.id)) || tc;
          const retrieval = retrievalMap.get(tc.id);
          seen.set(tc.id, {
            id: numbered.id,
            code: numbered.code,
            name: numbered.name,
            metricId: numbered.metricId,
            prompt: retrieval?.prompt || (Array.isArray(numbered.input) ? numbered.input[0] : "") || "",
            expected: unwrapResponseText(
              numbered.groundTruth?.expected_output || numbered.groundTruth?.expected_behavior || ""
            ),
            path: EXECUTION_PATHS[0],
            metricName: retrieval?.metricName || numbered.metricName || "",
            outcome: retrieval?.outcome || "pending",
            chunks: retrieval?.chunks || [],
            answer: retrieval?.answer || "",
            traces: retrieval?.traces || [],
            evidence: retrieval?.evidence || null,
            matchCount: retrieval?.matchCount ?? 0,
            usableCount: retrieval?.usableCount ?? 0,
            queried: retrieval?.queried || null,
            diagnostics: retrieval?.diagnostics || null,
            error: retrieval?.error || null,
            connectors: retrieval?.connectors || requiredConnectors,
            answerSource: retrieval?.answerSource || inferredAnswerSource,
          });
        })
      );
      return Array.from(seen.values());
    },
    [selectedItems, numberedById, requiredConnectors, inferredAnswerSource]
  );

  const drawerMetrics = useMemo(
    () => metrics.filter((m) => m.enabled !== false),
    [metrics]
  );

  const closeCustomTestCaseFlyout = useCallback(() => {
    setCustomTestCaseFlyoutOpen(false);
    closeScorecardMetricDetailRef.current?.();
    setScorecardMetricDetailOpen(false);
  }, []);

  const buildConnectionPayloadForCustomRun = useCallback(() => {
    const collectConnectors = activeConnectors(requiredConnectors, connectorChecks).filter(
      (id) => id !== CONNECTORS.TRACES
    );
    const usingGate1Vector = Boolean(gate1Source?.available);
    const vectorBuilt = buildRetrievePayload(vectorForm, vectorCredentials);
    const apiBuilt = buildApiPayload(apiForm, apiCredentials);
    const tracesBuilt = buildTracePayload(traceForm, traceCredentials);
    return {
      collectConnectors,
      connectionPayload: {
        connectors: collectConnectors,
        credentials: {
          ...(collectConnectors.includes(CONNECTORS.VECTOR_STORE)
            ? { vector: vectorBuilt.credentials }
            : {}),
          ...(collectConnectors.includes(CONNECTORS.REST_API) ? { api: apiBuilt.credentials } : {}),
          ...(collectConnectors.includes(CONNECTORS.TRACES) ? { traces: tracesBuilt.credentials } : {}),
        },
        ...(collectConnectors.includes(CONNECTORS.VECTOR_STORE) && !usingGate1Vector
          ? { vector_store_config: vectorBuilt.vector_store_config }
          : {}),
        ...(collectConnectors.includes(CONNECTORS.REST_API) ? { api_config: apiBuilt.api_config } : {}),
        ...(collectConnectors.includes(CONNECTORS.TRACES) ? { traces_config: tracesBuilt.traces_config } : {}),
        top_k: Number(vectorForm.topK) || vectorBuilt.vector_store_config.topK,
      },
    };
  }, [
    requiredConnectors,
    connectorChecks,
    gate1Source,
    vectorForm,
    vectorCredentials,
    apiForm,
    apiCredentials,
    traceForm,
    traceCredentials,
  ]);

  const runCustomTestCaseFlow = useCallback(
    async (mode, payload) => {
      closeCustomTestCaseFlyout();
      setCustomExecOpen(true);
      setCustomExecProgress(0);
      setError("");
      const { collectConnectors, connectionPayload } = buildConnectionPayloadForCustomRun();
      try {
        const outcome = await executeCustomTestCase({
          projectId,
          mode,
          payload,
          metrics: drawerMetrics,
          connectionPayload,
          collectConnectors,
          runId,
          setProgress: setCustomExecProgress,
        });
        if (mode === "runOnce") {
          setEphemeralCustomRunCount((n) => n + 1);
          setCustomTestCaseToast("Custom test case executed (not saved). Count updated on the scorecard.");
        } else {
          if (outcome.saveApiResponse?.data?.testCases) {
            setAllTestCases(outcome.saveApiResponse.data.testCases);
          }
          if (outcome.saveApiResponse?.data?.metrics?.length) {
            setMetrics(outcome.saveApiResponse.data.metrics);
          }
          setSavedCustomTestResults((prev) => [...prev, outcome.row]);
          setCustomTestCaseToast("Custom test case saved and executed.");
        }
      } catch (e) {
        if (e.code === "API_BEARER_REQUIRED" || e.code === "API_BEARER_REJECTED") {
          const prompt = readinessBearerPrompt(e.code);
          setConnectionAlert({
            title: prompt.title,
            message: prompt.message,
            openReadiness: true,
            actionLabel: "Update token in System Readiness",
          });
        }
        setError(e.message || "Custom test case execution failed.");
      } finally {
        setCustomExecOpen(false);
        setCustomExecProgress(0);
      }
    },
    [
      projectId,
      drawerMetrics,
      runId,
      closeCustomTestCaseFlyout,
      buildConnectionPayloadForCustomRun,
    ]
  );

  const handleCustomTestCaseSave = useCallback(
    (payload) => runCustomTestCaseFlow("saveExecute", payload),
    [runCustomTestCaseFlow]
  );

  const handleCustomTestCaseRunOnce = useCallback(
    (payload) => runCustomTestCaseFlow("runOnce", payload),
    [runCustomTestCaseFlow]
  );

  const patchMetricProgress = useCallback((key, status) => {
    setExecutionDialog((prev) => ({
      ...prev,
      metrics: prev.metrics.map((row) => (row.key === key ? { ...row, status } : row)),
    }));
  }, []);

  const returnToDefineScope = useCallback(() => {
    setDetailItemId("");
    setStepId("scope");
    setCompletedStepIds((prev) => {
      const next = new Set(prev);
      next.delete("scope");
      return next;
    });
  }, []);

  const handleConfirmScope = () => {
    if (!canRun) return;
    setDetailItemId("");
    setCompletedStepIds((prev) => new Set(prev).add("scope"));
    setStepId("assign");
  };

  const closeExecutionDialog = useCallback(() => {
    setExecutionDialog((prev) => ({ ...prev, open: false, phase: "running" }));
  }, []);

  const handleCancelExecution = useCallback(() => {
    executionCancelRef.current = true;
    setRunning(false);
    setScoring(false);
    closeExecutionDialog();
    setStepId("assign");
  }, [closeExecutionDialog]);

  const finishExecutionAndOpenScorecard = useCallback(() => {
    closeExecutionDialog();
    setCompletedStepIds((prev) => new Set(prev).add("assign"));
    setStepId("review");
    setScorecardToastOpen(true);
  }, [closeExecutionDialog]);

  /**
   * Proceed to Execute: Figma loader — per-metric evidence collection + scoring.
   */
  const handleProceedToExecute = async () => {
    if (running || !canRun) return;
    const cases = executionCases;
    if (!cases.length) return;

    executionCancelRef.current = false;
    const progressRows = listProgressMetrics(selectedItems, metricsById).map((row) => ({
      ...row,
      status: "pending",
    }));

    flushSync(() => {
      setExecutionDialog({
        open: true,
        phase: "running",
        metrics: progressRows,
        passCount: 0,
        failCount: 0,
      });
      setRunning(true);
      setScoring(true);
      setError("");
      setConnectionAlert(null);
      setScopeNote("");
    });

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    const collectConnectors = activeConnectors(requiredConnectors, connectorChecks).filter(
      (id) => id !== CONNECTORS.TRACES
    );
    const casesById = new Map(cases.map((tc) => [tc.id, tc]));
    const localRetrieval = new Map();

    if (!collectConnectors.length) {
      setScopeNote(
        requiredConnectors.length
          ? "No saved System Readiness connection was ready for this run, so retrieval was skipped."
          : "These metrics do not need a customer connection, so retrieval was skipped."
      );
      for (const tc of cases) {
        localRetrieval.set(tc.id, { outcome: "skipped", chunks: [], traces: [], answer: "" });
      }
      setRetrievalByCaseId(new Map(localRetrieval));
    } else {
      for (const tc of cases) {
        localRetrieval.set(tc.id, {
          outcome: "queued",
          chunks: [],
          traces: [],
          answer: "",
          connectors: collectConnectors,
          answerSource: inferredAnswerSource,
        });
      }
      setRetrievalByCaseId(new Map(localRetrieval));
    }

    const usingGate1Vector = Boolean(gate1Source?.available);
    const vectorBuilt = buildRetrievePayload(vectorForm, vectorCredentials);
    const apiBuilt = buildApiPayload(apiForm, apiCredentials);
    const tracesBuilt = buildTracePayload(traceForm, traceCredentials);
    const connectionPayload = {
      connectors: collectConnectors,
      credentials: {
        ...(collectConnectors.includes(CONNECTORS.VECTOR_STORE)
          ? { vector: vectorBuilt.credentials }
          : {}),
        ...(collectConnectors.includes(CONNECTORS.REST_API) ? { api: apiBuilt.credentials } : {}),
        ...(collectConnectors.includes(CONNECTORS.TRACES) ? { traces: tracesBuilt.credentials } : {}),
      },
      ...(collectConnectors.includes(CONNECTORS.VECTOR_STORE) && !usingGate1Vector
        ? { vector_store_config: vectorBuilt.vector_store_config }
        : {}),
      ...(collectConnectors.includes(CONNECTORS.REST_API)
        ? {
            api_config: apiBuilt.api_config,
          }
        : {}),
      ...(collectConnectors.includes(CONNECTORS.TRACES) ? { traces_config: tracesBuilt.traces_config } : {}),
      top_k: Number(vectorForm.topK) || vectorBuilt.vector_store_config.topK,
    };

    let currentRunId = null;
    setRunId(null);

    const CONNECTION_CODES = new Set([
      "VECTOR_STORE_UNREACHABLE",
      "VECTOR_STORE_CONFIG_REQUIRED",
      "VECTOR_STORE_CREDENTIAL_REQUIRED",
      "API_UNREACHABLE",
      "API_CONFIG_REQUIRED",
      "API_BEARER_REQUIRED",
      "API_BEARER_REJECTED",
      "APP_ENDPOINT_REQUIRED",
      "TRACE_BACKEND_UNREACHABLE",
      "TRACE_CONFIG_REQUIRED",
    ]);
    const ALERT_TITLE = {
      VECTOR_STORE_UNREACHABLE: "Cannot connect to the vector database",
      VECTOR_STORE_CONFIG_REQUIRED: "Cannot connect to the vector database",
      VECTOR_STORE_CREDENTIAL_REQUIRED: "Cannot connect to the vector database",
      API_UNREACHABLE: "Cannot connect to the application API",
      API_CONFIG_REQUIRED: "Cannot connect to the application API",
      API_BEARER_REQUIRED: "Bearer token required",
      API_BEARER_REJECTED: "Bearer token rejected",
      APP_ENDPOINT_REQUIRED: "Cannot connect to the application API",
      TRACE_BACKEND_UNREACHABLE: "Cannot connect to the trace backend",
      TRACE_CONFIG_REQUIRED: "Cannot connect to the trace backend",
    };

    const allJobs = flattenExecutionJobs(selectedItems, [], metricsById);
    const scoredKeys = new Set();
    const results = [];

    const finishMetric = async (jobKey) => {
      if (scoredKeys.has(jobKey)) return;
      scoredKeys.add(jobKey);
      const execSnapshot = buildExecutionCasesSnapshot(localRetrieval);
      const job = flattenExecutionJobs(selectedItems, execSnapshot, metricsById).find(
        (j) => j.key === jobKey
      );
      if (!job) {
        patchMetricProgress(jobKey, "fail");
        setExecutionDialog((prev) => ({ ...prev, failCount: prev.failCount + 1 }));
        return;
      }
      const result = await scoreSingleJob({
        projectId,
        runId: currentRunId,
        job,
        executionCases: execSnapshot,
      });
      results.push(result);
      const passed = metricPassed(result);
      patchMetricProgress(jobKey, passed ? "pass" : "fail");
      setExecutionDialog((prev) => ({
        ...prev,
        passCount: prev.passCount + (passed ? 1 : 0),
        failCount: prev.failCount + (passed ? 0 : 1),
      }));
    };

    try {
      for (const job of allJobs) {
        if (executionCancelRef.current) break;
        patchMetricProgress(job.key, "running");

        const collectIds = [...new Set(job.collectCaseIds || [])];
        if (collectConnectors.length) {
          for (const tcId of collectIds) {
            if (executionCancelRef.current) break;
            const testCase = casesById.get(tcId);
            if (!testCase) continue;

            localRetrieval.set(tcId, {
              ...(localRetrieval.get(tcId) || {}),
              outcome: "running",
              connectors: collectConnectors,
              answerSource: inferredAnswerSource,
            });
            setRetrievalByCaseId(new Map(localRetrieval));

            try {
              const res = await collectExecutionEvidence(projectId, {
                ...connectionPayload,
                ...(currentRunId ? { run_id: currentRunId } : {}),
                test_case_ids: [tcId],
              });
              if (res?.data?.runId && !currentRunId) {
                currentRunId = res.data.runId;
                setRunId(currentRunId);
              }
              const row = res?.data?.results?.[0];
              localRetrieval.set(tcId, {
                ...(localRetrieval.get(tcId) || {}),
                prompt: row?.prompt || testCase.prompt,
                metricName: row?.metricName || "",
                outcome:
                  row?.outcome ||
                  (row?.ok
                    ? row.usableCount > 0 || row.answer || (row.traces || []).length
                      ? "retrieved"
                      : "empty"
                    : "failed"),
                chunks: row?.chunks || [],
                answer: row?.answer || "",
                traces: row?.traces || [],
                evidence: row?.evidence || null,
                extras: row?.evalCase?.extras || testCase.extras || {},
                statusCode: row?.statusCode ?? row?.evidence?.httpStatus ?? null,
                latencyMs: row?.appLatencyMs ?? row?.retrievalLatencyMs ?? null,
                askOk: Boolean(row?.askOk),
                askError: row?.askError || null,
                matchCount: row?.matchCount ?? 0,
                usableCount: row?.usableCount ?? 0,
                queried: row?.queried || null,
                diagnostics: res?.data?.diagnostics || null,
                error: row?.error || null,
                connectors: row?.connectors || collectConnectors,
                answerSource: row?.answerSource || inferredAnswerSource,
              });
            } catch (err) {
              const data = err.response?.data;
              if (CONNECTION_CODES.has(data?.code)) {
                const bearerPrompt =
                  data.code === "API_BEARER_REQUIRED" || data.code === "API_BEARER_REJECTED"
                    ? readinessBearerPrompt(data.code)
                    : null;
                setConnectionAlert({
                  title: bearerPrompt?.title || ALERT_TITLE[data.code] || "Cannot connect",
                  message: bearerPrompt?.message || data.error,
                  details: bearerPrompt ? null : data.details,
                  openReadiness: Boolean(bearerPrompt),
                  actionLabel: bearerPrompt ? "Update token in System Readiness" : undefined,
                });
                setRetrievalByCaseId(new Map());
                handleCancelExecution();
                return;
              }
              localRetrieval.set(tcId, {
                ...(localRetrieval.get(tcId) || {}),
                outcome: "failed",
                chunks: [],
                traces: [],
                answer: "",
                error: {
                  type: "retrieval_failure",
                  message: data?.error || err.message || "Evidence collection failed for this test case.",
                },
              });
            }
            setRetrievalByCaseId(new Map(localRetrieval));
          }
        }

        if (executionCancelRef.current) break;
        await finishMetric(job.key);
      }

      if (!executionCancelRef.current) {
        for (const row of progressRows) {
          if (!scoredKeys.has(row.key)) {
            await finishMetric(row.key);
          }
        }
        setMetricResults(results);
        finishExecutionAndOpenScorecard();
      }
    } finally {
      setRunning(false);
      setScoring(false);
    }
  };

  const handleGateClick = (gateIndex) => {
    if (gateIndex === 0) navigate(`/chat/${projectId}`);
    if (gateIndex === 1) navigate(`/gate1/${projectId}`);
    if (gateIndex === 2) setStepId("scope");
  };

  const onScopeStep = stepId === "scope";
  const onAssignReviewStep = stepId === "assign";
  const onEvidenceStep = stepId === "review";
  const onDecisionStep = stepId === "decision";
  const goBackFromScorecardHeader = () => {
    if (onDecisionStep) {
      setStepId("review");
      return;
    }
    if (scorecardMetricDetailOpen) {
      closeScorecardMetricDetailRef.current();
      return;
    }
    setAssignPhase("review");
    setStepId("assign");
  };
  const scopeShellStep = onScopeStep || onAssignReviewStep;
  const gate2ScorecardFlowStep = onEvidenceStep || onDecisionStep;
  const gate2ShellStep = scopeShellStep || gate2ScorecardFlowStep;
  const progressPercent = Math.round((completedStepIds.size / GATE2_STEPS.length) * 100);

  const activeGate2StepLabel = useMemo(
    () => GATE2_STEPS.find((s) => s.id === stepId)?.label ?? "Gate 2",
    [stepId]
  );

  const customTestCaseCount = useMemo(
    () => ephemeralCustomRunCount + savedCustomTestResults.length,
    [ephemeralCustomRunCount, savedCustomTestResults.length]
  );

  const scorecardSummary = useMemo(
    () => buildScorecardSummary(metricResults, targetTrustIndex),
    [metricResults, targetTrustIndex]
  );

  const customFailedCount = useMemo(
    () => savedCustomTestResults.filter((r) => r.passed === false).length,
    [savedCustomTestResults]
  );

  const decisionTrustBreakdown = useMemo(
    () => trustScoreBreakdown(scorecardSummary.evaluated, metrics, trustWeights),
    [scorecardSummary.evaluated, metrics, trustWeights]
  );
  const decisionOverallScore = decisionTrustBreakdown ? decisionTrustBreakdown.score : null;

  useEffect(() => {
    if (!projectId || !decisionTrustBreakdown) return;
    let cancelled = false;
    logTrustScoreBreakdown(projectId, decisionTrustBreakdown).catch((err) => {
      if (!cancelled) {
        console.error("[gate2 trust score] failed to send breakdown to the server", err);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, decisionTrustBreakdown]);

  const decisionMeetsTarget = useMemo(() => {
    if (decisionOverallScore == null || targetTrustIndex == null) return false;
    return decisionOverallScore >= targetTrustIndex;
  }, [decisionOverallScore, targetTrustIndex]);

  const decisionHasGapFailures = useMemo(
    () => scorecardSummary.failedCount > 0 || customFailedCount > 0,
    [scorecardSummary.failedCount, customFailedCount]
  );

  const decisionCanSubmit = canSubmitGate2Decision({
    vote: decisionVote,
    rationale: decisionRationale,
    attestationChecked: decisionAttestation,
    meetsTarget: decisionMeetsTarget,
    hasGapFailures: decisionHasGapFailures,
  });

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: gate2ShellStep ? "#FFFFFF" : "#F5F5F5",
        minWidth: 0,
        overflow: gate2ShellStep ? "hidden" : undefined,
      }}
    >
      {gate2ShellStep && !loading ? (
        <Box
          component="a"
          href={`#${GATE2_MAIN_ID}`}
          sx={GATE2_SKIP_TO_MAIN_SX}
          onClick={(e) => {
            e.preventDefault();
            focusGate2MainContent();
          }}
        >
          Skip to main content
        </Box>
      ) : null}
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={ACTIVE_GATE}
        onGateClick={handleGateClick}
        onBack={
          stepId === "assign" && assignPhase === "execute"
            ? () => {
                setAssignPhase("review");
                setStepId("assign");
              }
            : () => navigate(`/test-suites/${projectId}`)
        }
        showBack={!gate2ShellStep}
        showViewSummary={false}
      />

      {!gate2ShellStep ? (
        <>
          <Gate2PageTitleBar
            onBack={
              stepId === "assign" && assignPhase === "execute"
                ? () => {
                    setAssignPhase("review");
                    setStepId("assign");
                  }
                : () => navigate(`/test-suites/${projectId}`)
            }
            backAriaLabel={
              stepId === "assign" && assignPhase === "execute" ? "Back to review" : "Back to test suites"
            }
          />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 2, md: 5 },
              px: { xs: 2, md: 3 },
              py: 2,
            }}
          >
            <StatBlock
              value={pad2(suites.length)}
              label="Test Suites Available"
              detail="Approved in Gate 1"
              color="#15803D"
            />
            <StatBlock
              value={pad2(EXECUTION_PATHS.length)}
              label="Execution Paths"
              detail={EXECUTION_PATHS.join(" · ")}
              color="#005BF0"
            />
            <StatBlock
              value={pad2(EVIDENCE_TIERS.length)}
              label="Evidence Tiers"
              detail={EVIDENCE_TIERS.join(" · ")}
              color="#7C3AED"
            />
            <StatBlock
              value={pad2(DECISION_QUORUM.length)}
              label="Decision Quorum"
              detail={`${DECISION_QUORUM.join(" + ")}, fixed`}
              color="#0F172A"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: { xs: 2, md: 3 },
              pb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                color: "#0F172A",
                whiteSpace: "nowrap",
              }}
            >
              Gate 2 Release Readiness
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: "#E2E8F0",
                "& .MuiLinearProgress-bar": { bgcolor: "#005BF0", borderRadius: 3 },
              }}
            />
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                color: "#0F172A",
                whiteSpace: "nowrap",
              }}
            >
              {progressPercent}% complete
            </Typography>
          </Box>
        </>
      ) : null}

      {error ? (
        <Typography sx={{ px: { xs: 2, md: 3 }, pt: 2, fontSize: 13, color: "#DE350B", fontWeight: 600 }}>
          {error}
        </Typography>
      ) : null}

      {loading && gate2ShellStep ? (
        <>
          {gate2ScorecardFlowStep ? (
            <Gate2PageTitleBar
              onBack={goBackFromScorecardHeader}
              backAriaLabel={
                onDecisionStep || scorecardMetricDetailOpen ? "Back to evidence" : "Back to review and execute"
              }
            />
          ) : onAssignReviewStep ? (
            <Gate2ReviewStickyHeader onBack={returnToDefineScope} />
          ) : (
            <Gate2ScopeStickyHeader onBack={() => navigate(`/test-suites/${projectId}`)} />
          )}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        </>
      ) : loading ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : gate2ShellStep ? (
        <>
          {gate2ScorecardFlowStep ? (
            onEvidenceStep && scorecardMetricDetail ? (
              <Gate2MetricDetailHeader item={scorecardMetricDetail} onBack={goBackFromScorecardHeader} />
            ) : (
            <Gate2PageTitleBar
              onBack={goBackFromScorecardHeader}
              backAriaLabel={
                onDecisionStep || scorecardMetricDetailOpen ? "Back to evidence" : "Back to review and execute"
              }
            />
            )
          ) : !detailItemId ? (
            onAssignReviewStep ? (
              <Gate2ReviewStickyHeader onBack={returnToDefineScope} />
            ) : (
              <Gate2ScopeStickyHeader onBack={() => navigate(`/test-suites/${projectId}`)} />
            )
          ) : null}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: { xs: "column", md: detailItemId ? "column" : "row" },
              overflow: "hidden",
              bgcolor: "#FFFFFF",
            }}
          >
            {!detailItemId && !scorecardMetricDetailOpen ? (
              <StepRail
                layout="sidebar"
                steps={GATE2_STEPS}
                activeStepId={stepId}
                completedStepIds={completedStepIds}
                onSelect={(id) => {
                  if (id === "scope") {
                    returnToDefineScope();
                    return;
                  }
                  if (id === "review" && !completedStepIds.has("assign")) return;
                  if (id === "decision" && !GATE2_DECISION_ENABLED) return;
                  if (id === "decision" && !completedStepIds.has("review")) return;
                  setStepId(id);
                }}
              />
            ) : null}
            <Box
              id={GATE2_MAIN_ID}
              component="main"
              tabIndex={-1}
              aria-label={`Gate 2: ${activeGate2StepLabel}`}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor:
                  gate2ScorecardFlowStep && !scorecardMetricDetailOpen ? "#FAFAFA" : "#FFFFFF",
                outline: "none",
              }}
            >
              {onDecisionStep ? (
                <Gate2DecisionStep
                  overallScore={decisionOverallScore}
                  targetTrustIndex={targetTrustIndex}
                  metricsPassedCount={scorecardSummary.passedCount}
                  metricsFailedCount={scorecardSummary.failedCount}
                  metricsTotal={scorecardSummary.total}
                  customFailedCount={customFailedCount}
                  customTotal={savedCustomTestResults.length}
                  vote={decisionVote}
                  onVoteChange={setDecisionVote}
                  rationale={decisionRationale}
                  onRationaleChange={setDecisionRationale}
                  attestationChecked={decisionAttestation}
                  onAttestationChange={setDecisionAttestation}
                />
              ) : onEvidenceStep ? (
                <QualityScorecardStep
                  results={metricResults}
                  casesById={new Map(executionCases.map((c) => [c.id, c]))}
                  testCasesById={numberedById}
                  executionCases={executionCases}
                  risks={risks}
                  metrics={metrics}
                  targetTrustIndex={targetTrustIndex}
                  customTestCaseCount={customTestCaseCount}
                  customTestCaseRows={savedCustomTestResults}
                  ephemeralCustomRunCount={ephemeralCustomRunCount}
                  onAddCustomTestCase={() => setCustomTestCaseFlyoutOpen(true)}
                  metricItems={metricItems}
                  onMetricDetailChange={handleScorecardMetricDetailChange}
                  projectId={projectId}
                  isContentGenerator={isContentGenerator}
                />
              ) : (
              <ExecutionScopeStep
                showPageHeader={false}
                confirmationMode={onAssignReviewStep}
                onBack={
                  onAssignReviewStep
                    ? returnToDefineScope
                    : () => navigate(`/test-suites/${projectId}`)
                }
                items={onAssignReviewStep ? selectedItems : items}
                scopeMode={scopeMode}
                onScopeModeChange={(mode) => {
                  setScopeMode(mode);
                  setDetailItemId("");
                }}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                builds={builds}
                detailItemId={detailItemId}
                onOpenDetails={setDetailItemId}
                onCloseDetails={() => setDetailItemId("")}
                testCasesById={numberedById}
                metrics={metrics}
                risks={risks}
                projectId={projectId}
                running={running}
                vectorOptional={isContentGenerator}
                appEndpoint={isContentGenerator ? appEndpoint : null}
                requiredConnectors={requiredConnectors.filter((id) => id !== CONNECTORS.TRACES)}
                apiForm={apiForm}
                apiCredentials={apiCredentials}
                onApiFormChange={setApiForm}
                onApiCredentialsChange={setApiCredentials}
                gate1ApiSource={gate1ApiSource}
                onOpenGate1Api={() =>
                  navigate(gate1ReadinessConnectorPath(projectId, GATE1_CONNECTOR_SURFACE.api))
                }
                vectorForm={vectorForm}
                vectorCredentials={vectorCredentials}
                onVectorFormChange={setVectorForm}
                onVectorCredentialsChange={setVectorCredentials}
                gate1Source={gate1Source}
                onOpenGate1Vector={() =>
                  navigate(gate1ReadinessConnectorPath(projectId, GATE1_CONNECTOR_SURFACE.vector))
                }
              />
              )}
            </Box>
          </Box>
        </>
      ) : null}

      {/* Scope footer ΓÇö the confirm action that starts the run */}
      {!loading && stepId === "scope" ? (
        <Box
          component="footer"
          aria-label="Define scope actions"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: 56,
            boxSizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 0,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E6E6E6",
            boxShadow: "0px -1px 8px 0px #2122260A",
            flexShrink: 0,
          }}
        >
          {detailItemId ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.5,
                width: "100%",
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={() => setDetailItemId("")}
                sx={SCOPE_CANCEL_BTN_SX}
              >
                Back
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Button
                  variant="outlined"
                  disableElevation
                  onClick={() => navigate(`/test-suites/${projectId}`)}
                  sx={SCOPE_CANCEL_BTN_SX}
                >
                  Back
                </Button>
                {scopeFooterHint ? (
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontSize: 13,
                      color: "#64748B",
                      minWidth: 0,
                    }}
                  >
                    {scopeFooterHint}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!canRun}
                  onClick={handleConfirmScope}
                  sx={PRIMARY_BTN_SX}
                >
                  Confirm Scope
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : null}

      {!loading && onAssignReviewStep ? (
        <Box
          component="footer"
          aria-label="Review and execute actions"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: 56,
            boxSizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 0,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E6E6E6",
            boxShadow: "0px -1px 8px 0px #2122260A",
            flexShrink: 0,
          }}
        >
          {detailItemId ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.5,
                width: "100%",
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={() => setDetailItemId("")}
                sx={SCOPE_CANCEL_BTN_SX}
              >
                Back
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Button
                  variant="outlined"
                  disableElevation
                  onClick={returnToDefineScope}
                  sx={SCOPE_CANCEL_BTN_SX}
                >
                  Back
                </Button>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!canRun || running}
                  onClick={handleProceedToExecute}
                  sx={PRIMARY_BTN_SX}
                >
                  Proceed to Execute
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : null}

      {!loading && onEvidenceStep && gate2ShellStep ? (
        <Box
          component="footer"
            aria-label="Evidence scorecard actions"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: 56,
            boxSizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 0,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E6E6E6",
            boxShadow: "0px -1px 8px 0px #2122260A",
            flexShrink: 0,
          }}
        >
          {scorecardMetricDetailOpen ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.5,
                width: "100%",
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                onClick={() => closeScorecardMetricDetailRef.current()}
                sx={SCOPE_CANCEL_BTN_SX}
              >
                Back
              </Button>
            </Box>
          ) : (
            <>
              <Button
                variant="outlined"
                disableElevation
                onClick={() => {
                  setStepId("assign");
                }}
                sx={SCOPE_CANCEL_BTN_SX}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disableElevation
                disabled={!GATE2_DECISION_ENABLED}
                onClick={() => {
                  const meets =
                    decisionOverallScore != null &&
                    targetTrustIndex != null &&
                    decisionOverallScore >= targetTrustIndex;
                  setDecisionVote(meets ? "go" : "conditional_go");
                  setDecisionRationale("");
                  setDecisionAttestation(false);
                  setCompletedStepIds((prev) => new Set(prev).add("review"));
                  setStepId("decision");
                }}
                sx={PRIMARY_BTN_SX}
              >
                Proceed to Decision
              </Button>
            </>
          )}
        </Box>
      ) : null}

      {!loading && onDecisionStep && gate2ShellStep ? (
        <Box
          component="footer"
          aria-label="Decision actions"
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            height: 56,
            boxSizing: "border-box",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, md: 3 },
            py: 0,
            bgcolor: "#FFFFFF",
            borderTop: "1px solid #E6E6E6",
            boxShadow: "0px -1px 8px 0px #2122260A",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexShrink: 0,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              disableElevation
              onClick={() => setStepId("review")}
              sx={SCOPE_CANCEL_BTN_SX}
            >
              Back
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!decisionCanSubmit}
              onClick={() => {
                const done = new Set(completedStepIds).add("decision");
                setCompletedStepIds(done);
                writeGate2Session(projectId, {
                  stepId: "decision",
                  scopeMode,
                  selectedByMetric: [...selectedByMetric],
                  selectedBySuite: [...selectedBySuite],
                  completedStepIds: [...done],
                  decisionVote,
                  decisionRationale,
                  decisionAttestation,
                  runId,
                });
                navigate(`/gate3/${projectId}`, {
                  state: {
                    vote: decisionVote,
                    decidedAt: new Date().toISOString(),
                  },
                });
              }}
              sx={PRIMARY_BTN_SX}
            >
              Confirm &amp; Submit
            </Button>
          </Box>
        </Box>
      ) : null}

      <Snackbar
        open={Boolean(decisionSubmitNotice)}
        autoHideDuration={6000}
        onClose={() => setDecisionSubmitNotice("")}
        message={decisionSubmitNotice}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <AppToast
        open={scorecardToastOpen && onEvidenceStep}
        autoHideMs={8000}
        onClose={() => setScorecardToastOpen(false)}
        title={`${scorecardSummary.total} metric${scorecardSummary.total === 1 ? "" : "s"} calculated successfully`}
        subtitle={
          scorecardSummary.failedCount > 0
            ? `${scorecardSummary.failedCount} of ${scorecardSummary.total} failed threshold — review flagged items.`
            : "All evaluated metrics met their thresholds."
        }
      />

      <Gate2ExecutingMetricsDialog
        open={executionDialog.open}
        metrics={executionDialog.metrics}
        onCancel={handleCancelExecution}
      />

      <Gate2ExecutingCustomTestCaseDialog open={customExecOpen} progress={customExecProgress} />

      <VectorConnectionAlert
        alert={connectionAlert}
        onClose={() => setConnectionAlert(null)}
        onAction={
          connectionAlert?.openReadiness
            ? () => {
                setConnectionAlert(null);
                navigate(gate1ReadinessConnectorPath(projectId, GATE1_CONNECTOR_SURFACE.api));
              }
            : undefined
        }
      />

      <DeclareBuildDialog
        suite={buildDialogItem}
        build={buildDialogItem ? builds.get(buildDialogItem.id) : null}
        onClose={() => setBuildDialogItem(null)}
        onSave={(build) => {
          setBuilds((prev) => new Map(prev).set(buildDialogItem.id, build));
          // Declaring a build is the user saying "this one is in the run".
          setSelectedIds((prev) => new Set(prev).add(buildDialogItem.id));
          setBuildDialogItem(null);
        }}
      />

      <AddTestCaseDialog
        open={customTestCaseFlyoutOpen}
        onClose={closeCustomTestCaseFlyout}
        onSave={handleCustomTestCaseSave}
        onRunOnce={handleCustomTestCaseRunOnce}
        metrics={drawerMetrics}
        risks={risks}
        flyoutVariant="gate2Custom"
        isContentGenerator={isContentGenerator}
      />

      <AppToast
        open={Boolean(customTestCaseToast)}
        autoHideMs={6000}
        onClose={() => setCustomTestCaseToast("")}
        title={customTestCaseToast}
      />
    </Box>
  );
}
